<template>
  <div class="app-container"  :dir="locale === 'ar' ? 'rtl' : 'ltr'" :class="{'rtl': locale === 'ar', 'ltr': locale === 'en'}">
    <template v-if="showLayout">
      <NavbarComponent @logout="handleLogout" />

      <div class="main-content-wrapper">
        <SidebarComponent />
        
        <main class="main-content">
          <RenewalBanner />
          <Breadcrumbs />
          <router-view :key="$route.fullPath" />
        </main>
      </div>
      
      <FooterComponent />
    </template>
    
    <router-view v-else />
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import NavbarComponent from "@/components/common/NavbarComponent.vue";
import SidebarComponent from "@/components/common/SidebarComponent.vue";
import FooterComponent from "../common/FooterComponent.vue";
import Breadcrumbs from "@/components/ui/BreadcrumbsComponent.vue";
import RenewalBanner from "@/components/common/RenewalBanner.vue";
const router = useRouter();
const route = useRoute();
import { useI18n } from 'vue-i18n';
const { locale } = useI18n();

const showLayout = computed(() => {
  return route.meta.hideLayout !== true;
});

const handleLogout = () => {
  router.push("/login");
};

const updateDocumentDirection = (newLocale) => {
  document.documentElement.lang = newLocale;
  document.documentElement.dir = newLocale === 'ar' ? 'rtl' : 'ltr';
};

onMounted(() => {
  const savedLocale = localStorage.getItem('locale') || 'ar';
  locale.value = savedLocale;
  updateDocumentDirection(savedLocale);
});

watch(locale, (newLocale) => {
  updateDocumentDirection(newLocale);
});
</script>

<style scoped>

.rtl {
  direction: rtl;
  text-align: right;
}

.ltr {
  direction: ltr;
  text-align: left;
}

/* Ensure form elements respect direction */
.rtl .form-control,
.rtl .form-select {
  text-align: right;
  direction: rtl;
}

.ltr .form-control,
.ltr .form-select {
  text-align: left;
  direction: ltr;
}

/* Fix icon positioning */
.rtl .position-absolute.start-0 {
  right: 0 !important;
  left: auto !important;
}

.rtl .position-absolute.end-0 {
  left: 0 !important;
  right: auto !important;
}

.ltr .position-absolute.start-0 {
  left: 0 !important;
  right: auto !important;
}

.ltr .position-absolute.end-0 {
  right: 0 !important;
  left: auto !important;
}
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.main-content-wrapper {
  display: flex;
  flex: 1;
  /* margin-top: 60px; Navbar height */
}

.main-content {
  flex: 1;
  min-width: 0;
  padding: 24px;
  transition: all 0.3s ease;
  background-color: white;
  min-height: calc(100vh - 120px); /* Adjust based on header/footer height */
}

/* LTR specific styles */
[dir="ltr"] .main-content {
  margin-left: 280px; /* Sidebar width */
}

/* RTL specific styles */
[dir="rtl"] .main-content {
  margin-right: 280px; /* Sidebar width */
}

/* Responsive adjustments */
@media (max-width: 1199.98px) {
  .main-content {
    padding: 20px;
  }
}

@media (max-width: 991.98px) {
  .main-content {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 16px;
  }
}

@media (max-width: 767.98px) {
  .main-content {
    padding: 12px;
  }
}
</style>
